import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Divider,
} from "@mui/material";

import axios from "axios";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useThemeMode } from "../context/ThemeContext";
import { getApiErrorMessage } from "../utils/apiError";
import { SIGNUP_ROLES, ROLE_LABELS, ROLE_SELECT_MENU_PROPS } from "../utils/roleOptions";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const AUTH_BASE_URL = "/api/auth";

export default function Signup() {
  const { mode } = useThemeMode();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState(["brd"]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${AUTH_BASE_URL}/register`, { email, password, roles });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err, "Registration failed. Try again."));
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      fontSize: "0.9rem",
      bgcolor: mode === "light" ? "#f9fafb" : "rgba(255,255,255,0.03)",
      transition: "box-shadow 0.2s",
      "& fieldset": { borderColor: mode === "light" ? "#e5e7eb" : "rgba(255,255,255,0.08)" },
      "&:hover fieldset": { borderColor: "#F26522" },
      "&.Mui-focused fieldset": { borderColor: "#F26522", borderWidth: "1.5px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(242,101,34,0.10)" },
    },
  };

  const labelSx = {
    fontWeight: 600, fontSize: "0.82rem",
    color: mode === "light" ? "#374151" : "#94a3b8",
    mb: 0.7,
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>

      {/* ── Left Branding Panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "50%",
          minWidth: 440,
          background: "linear-gradient(145deg, #060e1e 0%, #0a1628 40%, #0e1f40 70%, #0d1835 100%)",
          position: "relative",
          overflow: "hidden",
          p: "40px 48px",
        }}
      >
        {/* Glow orbs */}
        <Box sx={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.18) 0%, transparent 65%)", top: -160, right: -180, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.10) 0%, transparent 65%)", bottom: -80, left: -120, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.12) 0%, transparent 65%)", bottom: "38%", right: "12%", pointerEvents: "none" }} />

        {/* Dot grid */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)", backgroundSize: "30px 30px", pointerEvents: "none" }} />

        {/* Top stripe accent */}
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, transparent 0%, #F26522 40%, #ff9a5c 70%, transparent 100%)" }} />

        {/* Logo */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <img src="/edge-ai-logo-white.png" alt="Assist-Edgex" style={{ height: 28, objectFit: "contain" }} />
        </Box>

        {/* Hero */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, bgcolor: "rgba(242,101,34,0.12)", border: "1px solid rgba(242,101,34,0.25)", borderRadius: "20px", px: 1.5, py: 0.5, mb: 2.5 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: "#F26522" }} />
            <Typography sx={{ color: "#F26522", fontSize: "0.72rem", fontWeight: 700, letterSpacing: 0.5 }}>
              ENTERPRISE AI PLATFORM
            </Typography>
          </Box>

          <Typography sx={{ color: "#fff", fontWeight: 800, lineHeight: 1.18, mb: 2, fontSize: { md: "2.1rem", lg: "2.6rem" }, letterSpacing: "-0.5px" }}>
            Start Your AI{" "}
            <Box component="span" sx={{ color: "#F26522", position: "relative", "&::after": { content: '""', position: "absolute", bottom: -4, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #F26522, transparent)", borderRadius: "2px" } }}>
              Automation Journey
            </Box>
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.5)", mb: 4, fontSize: "0.9rem", lineHeight: 1.75, maxWidth: 400 }}>
            Join thousands of enterprises automating smarter with the power of enterprise-grade AI automation.
          </Typography>

          {/* Compliance badges */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {["ISO 27001", "SOC 2", "GDPR", "HIPAA"].map((badge) => (
              <Box key={badge} sx={{ px: 1.4, py: 0.4, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#4ade80" }} />
                <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", fontWeight: 600 }}>{badge}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.72rem" }}>
            © {new Date().getFullYear()} AutomationEdge Technologies. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: mode === "light" ? "#f0f2f7" : "#070f1d",
          px: { xs: 3, sm: 5 },
          py: 6,
          overflowY: "auto",
          position: "relative",
        }}
      >
        {/* Subtle background pattern */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(242,101,34,0.04) 1px, transparent 1px)", backgroundSize: "24px 24px", pointerEvents: "none" }} />

        {/* Form card */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 420,
            bgcolor: mode === "light" ? "#ffffff" : "#0d1a2e",
            borderRadius: "20px",
            border: mode === "light" ? "1px solid rgba(0,0,0,0.07)" : "1px solid rgba(255,255,255,0.06)",
            boxShadow: mode === "light"
              ? "0 8px 40px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)"
              : "0 24px 60px rgba(0,0,0,0.4)",
            p: { xs: 3.5, sm: 4.5 },
          }}
        >
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 3 }}>
            <img src={mode === "light" ? "/edge-ai-logo-black.png" : "/edge-ai-logo-white.png"} alt="Assist-Edgex" style={{ height: 26, objectFit: "contain" }} />
          </Box>

          {success ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(242,101,34,0.1)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5 }}>
                <TaskAltIcon sx={{ fontSize: 36, color: "#F26522" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: mode === "light" ? "#0d1f3c" : "#f1f5f9" }}>
                You're all set! 🎉
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                Registration successful. Redirecting to login...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, color: mode === "light" ? "#0d1f3c" : "#f1f5f9", letterSpacing: "-0.3px" }}>
                Create account ✨
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 3.5 }}>
                Register for your AI workspace
              </Typography>

              {error && (
                <Box sx={{ bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "10px", px: 2, py: 1.2, mb: 2.5 }}>
                  <Typography color="error" variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                    {error}
                  </Typography>
                </Box>
              )}

              <Box component="form" onSubmit={handleSignup}>
                {/* Email */}
                <Typography variant="body2" sx={labelSx}>Email Address</Typography>
                <TextField
                  required fullWidth id="email" placeholder="you@company.com"
                  name="email" autoComplete="email" autoFocus variant="outlined"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ ...inputSx, mb: 2.2 }}
                />

                {/* Password */}
                <Typography variant="body2" sx={labelSx}>Password</Typography>
                <TextField
                  required fullWidth name="password" placeholder="••••••••"
                  type={showPassword ? "text" : "password"} id="password"
                  autoComplete="new-password" variant="outlined"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small" sx={{ color: "text.secondary" }}>
                            {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ ...inputSx, mb: 2.2 }}
                />

                {/* Role */}
                <Typography variant="body2" sx={labelSx}>Select Roles</Typography>
                <FormControl fullWidth required sx={{ mb: 0.5 }}>
                  <Select
                    id="role-select"
                    multiple
                    value={roles}
                    onChange={(e) => {
                      const next = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                      if (next.length > 0) setRoles(next);
                    }}
                    renderValue={(selected) => selected.map((role) => ROLE_LABELS[role] || role).join(", ")}
                    MenuProps={ROLE_SELECT_MENU_PROPS}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.5 }}>
                        <PersonOutlineIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: "10px",
                      fontSize: "0.9rem",
                      bgcolor: mode === "light" ? "#f9fafb" : "rgba(255,255,255,0.03)",
                      transition: "box-shadow 0.2s",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: mode === "light" ? "#e5e7eb" : "rgba(255,255,255,0.08)" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#F26522" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F26522", borderWidth: "1.5px" },
                      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(242,101,34,0.10)" },
                    }}
                  >
                    {SIGNUP_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Sign Up button */}
                <Button
                  type="submit" fullWidth variant="contained"
                  disabled={loading || success}
                  sx={{
                    mt: 3, mb: 2, py: 1.55, fontSize: "0.95rem", fontWeight: 700,
                    borderRadius: "10px", textTransform: "none",
                    background: "linear-gradient(90deg, #e8581a 0%, #F26522 50%, #ff8c42 100%)",
                    backgroundSize: "200% auto",
                    boxShadow: "0 4px 20px rgba(242,101,34,0.35)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundPosition: "right center",
                      boxShadow: "0 6px 28px rgba(242,101,34,0.50)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "translateY(0)", boxShadow: "0 2px 12px rgba(242,101,34,0.3)" },
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Create Account"}
                </Button>

                <Divider sx={{ my: 0.5, borderColor: mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)" }} />

                <Box textAlign="center" mt={2}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                    Already have an account?{" "}
                    <Link component={RouterLink} to="/login" underline="none"
                      sx={{ color: "#F26522", fontWeight: 700, "&:hover": { color: "#d95a18" } }}
                    >
                      Sign in
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
